/* =====================================================================
 * DocxRun — 텍스트 런 렌더링 (볼드/이탤릭/색상/폰트 등)
 * ===================================================================== */

import React from 'react';
import type { DocxRun as DocxRunType, DocxDocument } from '@auw/workspace-utils';
import { halfPointsToPx } from '@auw/workspace-utils';

interface DocxRunComponentProps {
  run: DocxRunType;
  runIndex: number;
  document: DocxDocument;
  inTable?: boolean;
}

/** Word 하이라이트 색상 → CSS 색상 */
const highlightMap: Record<string, string> = {
  yellow: '#ffff00',
  green: '#00ff00',
  cyan: '#00ffff',
  magenta: '#ff00ff',
  blue: '#0000ff',
  red: '#ff0000',
  darkBlue: '#000080',
  darkCyan: '#008080',
  darkGreen: '#008000',
  darkMagenta: '#800080',
  darkRed: '#800000',
  darkYellow: '#808000',
  darkGray: '#808080',
  lightGray: '#c0c0c0',
  black: '#000000',
  white: '#ffffff',
};

function getFontSizeScale(fontFamily?: string): number {
  const family = (fontFamily || '').toLowerCase();
  if (family.includes('arial unicode')) return 0.95;
  return 1;
}

export const DocxRunComponent: React.FC<DocxRunComponentProps> = ({
  run,
  runIndex,
  document: doc,
  inTable = false,
}) => {
  if (run.type === 'break') {
    return <br />;
  }

  if (run.type === 'tab') {
    return <span style={{ whiteSpace: 'pre' }}>{'\t'}</span>;
  }

  if (run.type === 'image') {
    const src = run.imageId ? doc.media.get(run.imageId) : undefined;
    if (!src) return null;
    return (
      <img
        src={src}
        alt=""
        style={{
          width: run.imageWidth ? `${run.imageWidth}px` : 'auto',
          height: run.imageHeight ? `${run.imageHeight}px` : 'auto',
          maxWidth: '100%',
          verticalAlign: 'bottom',
        }}
        draggable={false}
      />
    );
  }

  // type === 'text'
  const style: React.CSSProperties = {};

  if (run.bold) style.fontWeight = 'bold';
  if (run.italic) style.fontStyle = 'italic';
  if (run.underline) style.textDecoration = (style.textDecoration || '') + ' underline';
  if (run.strike) style.textDecoration = (style.textDecoration || '') + ' line-through';
  const resolvedFontFamily = run.fontFamily || doc.docDefaults?.fontFamily;
  if (run.fontSize) {
    const sizePt = halfPointsToPx(run.fontSize) * (inTable ? getFontSizeScale(resolvedFontFamily) : 1);
    style.fontSize = `${sizePt}pt`;
  }
  if (resolvedFontFamily) style.fontFamily = resolvedFontFamily;
  if (run.color) style.color = run.color;
  if (run.highlight) {
    style.backgroundColor = highlightMap[run.highlight] || run.highlight;
  }

  // 공백 보존
  if (run.text && (run.text.startsWith(' ') || run.text.endsWith(' ') || run.text.includes('  '))) {
    style.whiteSpace = 'pre-wrap';
  }

  return <span data-run-index={runIndex} style={style}>{run.text}</span>;
};
